import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
  isLoading?: boolean;
  className?: string;
}

export const Pagination = ({
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange,
  isLoading = false,
  className,
}: PaginationProps) => {
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  // Smart page number rendering for large datasets
  const renderPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 7;

    if (totalPages <= maxVisiblePages) {
      // Show all pages if total is small
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Smart pagination: 1 ... 5 6 7 ... 20
      const startPage = Math.max(1, currentPage - 2);
      const endPage = Math.min(totalPages, currentPage + 2);

      // Always show first page
      if (startPage > 1) {
        pages.push(1);
        if (startPage > 2) {
          pages.push("...");
        }
      }

      // Show pages around current page
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }

      // Always show last page
      if (endPage < totalPages) {
        if (endPage < totalPages - 1) {
          pages.push("...");
        }
        pages.push(totalPages);
      }
    }

    return pages.map((page, index) => {
      if (page === "...") {
        return (
          <span key={`ellipsis-${index}`} className="px-3 py-2 text-muted-foreground">
            ...
          </span>
        );
      }

      return (
        <Button
          key={page}
          variant={currentPage === page ? "default" : "outline"}
          size="sm"
          onClick={() => onPageChange(page as number)}
          disabled={isLoading}
          className="min-w-[32px] h-8 sm:min-w-[40px] sm:h-9 text-xs sm:text-sm px-2 sm:px-3"
        >
          {page}
        </Button>
      );
    });
  };

  return (
    <div className={cn("space-y-3", className)}>
      {/* Records information */}
      <div className="flex flex-col sm:flex-row items-center justify-between text-xs sm:text-sm text-muted-foreground gap-2 sm:gap-0 text-center sm:text-left">
        {isLoading ? (
          <Skeleton className="h-4 w-40" />
        ) : (
          <span>
            Показано {startItem}-{endItem} из {totalItems} записей
          </span>
        )}

        <span>
          Страница {currentPage} из {totalPages}
        </span>
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-center gap-1 flex-wrap">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage <= 1 || isLoading}
          className="mr-1 sm:mr-2 h-8 w-8 sm:h-9 sm:w-auto sm:px-3 p-0 sm:p-0"
        >
          <ChevronLeft className="h-4 w-4" />
          <span className="sr-only">Назад</span>
        </Button>

        {renderPageNumbers()}

        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage >= totalPages || isLoading}
          className="ml-1 sm:ml-2 h-8 w-8 sm:h-9 sm:w-auto sm:px-3 p-0 sm:p-0"
        >
          <ChevronRight className="h-4 w-4" />
          <span className="sr-only">Вперед</span>
        </Button>
      </div>
    </div>
  );
};
